<template>
    <div>
        <button
            ref="localeDropdownButtonElm"
            class="focus:outline-none focus:ring-none hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 rounded-md text-xs pl-2 pr-1 py-1 text-center inline-flex items-center text-gray-500 cursor-pointer"
            type="button"
            @mouseover="onMouseOver"
            @mouseleave="onMouseLeave">
            <flag-icon
                :language="selectedFlag"
                class="drop-shadow"
                :class="{
                    'w-4 h-4': props.size !== 'lg',
                    'w-5 h-5': props.size === 'lg',
                }"></flag-icon>
            <span
                v-if="props.showText"
                class="ml-2 dark:text-gray-300"
                :class="{
                    'text-sm': props.size === 'sm',
                    'text-base': props.size === 'base',
                    'text-lg': props.size === 'lg',
                }">
                {{ selectedLangNative }}</span
            >
            <i-tabler-chevron-down
                v-if="!dropdownOpened"
                class="w-5 h-5 ms-2 icon-stroke-2"></i-tabler-chevron-down>
            <i-tabler-chevron-up
                v-else
                class="w-5 h-5 ms-2"></i-tabler-chevron-up>
        </button>

        <tooltip-bubble id="tooltip-select-locale" ref="tooltipLocaleElm">{{
            $t('select_language_tooltip')
        }}</tooltip-bubble>

        <!-- Dropdown menu -->
        <div
            ref="localeDropdownMenuElm"
            class="z-10 hidden bg-white rounded-lg shadow-xl dark:bg-gray-700 max-h-[450px] flex flex-col"
            :class="{
                'w-[250px]': props.size === 'base',
                'w-[220px]': props.size === 'sm',
                'w-[270px]': props.size === 'lg',
            }">
            <div class="py-3 px-5 text-sm rounded-t-lg relative">
                <i-tabler-search
                    class="absolute top-5 left-7 pointer-events-none w-5 h-5 text-gray-400"></i-tabler-search>
                <input
                    type="text"
                    id="lang-search"
                    ref="langSearchElm"
                    class="block w-full px-2 py-1.5 ps-9 text-base text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-gray-500 dark:focus:border-gray-500"
                    v-model="searchQuery" />
            </div>

            <div
                class="space-y-1 text-sm text-gray-700 dark:text-gray-200 p-2 overflow-y-auto grid- grid-cols-2- gap-2- grid-rows-[auto-fill]-"
                :class="props.dropdownClasses">
                <div
                    v-for="(item, index) in filteredLanguages"
                    :key="index"
                    class="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex"
                    :class="{
                        'bg-[#e3e7ff] dark:bg-[#5967c1] text-gray-900 dark:text-gray-200':
                            selectedLang === item.code,
                    }"
                    @click="onSelect(item.code)"
                    :id="`lang-elm-${item.code}`">
                    <div
                        class="ms-2 text-sm me-2 inline-flex gap-2 flex-start w-full">
                        <flag-icon
                            :language="item.flag_code.toLowerCase()"
                            class="w-6 h-6 drop-shadow shrink-0 mt-[2px]"></flag-icon>
                        <div
                            class="font-medium text-gray-700 dark:text-gray-200 cursor-pointer"
                            :class="{
                                'text-base': props.size === 'base',
                                'text-sm': props.size === 'sm',
                                'text-lg': props.size === 'lg',
                            }">
                            <div class="break-all">
                                {{ item.lang_native }}
                            </div>
                            <div
                                v-if="item.lang_en !== item.lang_native"
                                class="break-all text-gray-500 dark:text-gray-400 font-normal"
                                :class="{
                                    'text-xs': props.size === 'sm',
                                    'text-sm': props.size === 'base',
                                    'text-base': props.size === 'lg',
                                }">
                                {{ item.lang_en }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import TooltipBubble from '~/components/TooltipBubble.vue';
import { Dropdown, Tooltip } from 'flowbite';
import type {
    DropdownOptions,
    DropdownInterface,
    InstanceOptions,
} from 'flowbite';
import { appLanguages, setI18nLanguage } from '~/helpers/i18n';
import type { LanguageCode } from '~/helpers/types';
import { debounce } from '~/helpers/utils';

interface Props {
    lang?: LanguageCode;
    showText?: boolean;
    size?: 'lg' | 'base' | 'sm';
    dropdownClasses?: string;
}

const props = withDefaults(defineProps<Props>(), {
    lang: 'en',
    showText: true,
    size: 'lg',
    dropdownClasses: '',
});

const emit = defineEmits(['update']);
const langSearchElm = ref<HTMLInputElement | null>(null);
const selectedLang = ref(props.lang);
let tooltip: Tooltip | null = null;
const dropdownOpened = ref(false);
const tooltipLocaleElm = ref<typeof TooltipBubble | null>(null);
const searchQuery = ref('');

// filter the languages based on the search query
const filteredLanguages = computed(() => {
    if (searchQuery.value === '') {
        return appLanguages;
    }
    const q = searchQuery.value.toLowerCase();
    return appLanguages.filter(
        (lang) =>
            lang.lang_en.toLowerCase().includes(q) ||
            lang.lang_native.toLowerCase().includes(q)
    );
});

// select language
const onSelect = async (langCode: LanguageCode) => {
    selectedLang.value = langCode;
    // setLocale(langCode);
    setI18nLanguage(langCode);
    dropdown?.hide();
    emit('update', langCode);
};

const selectedFlag = computed(() => {
    let code = appLanguages.find(
        (lang) => lang.code === selectedLang.value
    )?.flag_code;
    return code ? code : '';
});

const selectedLangNative = computed(() => {
    let lang = appLanguages.find(
        (lang) => lang.code === selectedLang.value
    )?.lang_native;
    return lang ? lang : '';
});

// set the dropdown menu element
const localeDropdownButtonElm = ref<HTMLElement | null>(null);
// set the element that trigger the dropdown menu on click
const localeDropdownMenuElm = ref<HTMLElement | null>(null);

// dropdown options
const options: DropdownOptions = {
    placement: 'bottom',
    triggerType: 'click',
    offsetSkidding: -20,
    offsetDistance: 10,
    delay: 300,
    onShow: () => {
        tooltip?.hide();
        tooltip?.destroy();
        tooltip = null;
        dropdownOpened.value = true;
        langSearchElm.value?.focus();
        // scroll to the selected language (#lang-elm-${selectedLang.value})
        const selectedLangElm = document.getElementById(
            `lang-elm-${selectedLang.value}`
        );
        if (selectedLangElm) {
            selectedLangElm.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'center',
            });
        }
        // resize the dropdown menu to fit the window
        // (causes flickering without timeout)
        setTimeout(() => {
            onWindowResize();
        }, 1000);
    },
    onHide: () => {
        dropdownOpened.value = false;
    },
};

// dropdown instance options object
const instanceOptions: InstanceOptions = {
    id: 'localeDropdownMenu',
    override: true,
};

let dropdown: DropdownInterface | null = null;

const onMouseOver = (e: MouseEvent) => {
    if (tooltip === null) {
        tooltip = new Tooltip(
            tooltipLocaleElm?.value?.$el,
            e.target as HTMLElement,
            {
                placement: 'bottom',
                triggerType: 'none',
            }
        );
        tooltip.show();
    }
};

const onMouseLeave = () => {
    tooltip?.hide();
    tooltip?.destroy();
    tooltip = null;
};

watch(
    () => props.lang,
    (newLang) => {
        selectedLang.value = newLang;
    }
);

const onWindowResize = debounce(() => {
    // if bottom edge of dropdownMenuElm.value is out of window,
    // update the height of the dropdown menu
    if (localeDropdownMenuElm.value) {
        const rect = localeDropdownMenuElm.value.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const bottomEdge = rect.bottom;

        if (bottomEdge > windowHeight) {
            localeDropdownMenuElm.value.style.maxHeight =
                windowHeight - rect.top - 30 + 'px';
        } else {
            localeDropdownMenuElm.value.style.maxHeight = '';
        }
    }
}, 100);

onMounted(async () => {
    window.addEventListener('resize', onWindowResize, false);

    dropdown = new Dropdown(
        localeDropdownMenuElm.value,
        localeDropdownButtonElm.value,
        options,
        instanceOptions
    );
});

onUnmounted(() => {
    window.removeEventListener('resize', onWindowResize, false);
});
</script>
